import type {
  FeedbackCatalogMailDefinition,
  FeedbackTemplateLocale,
  ResolvedFeedbackMailCatalogItem,
} from './types';

/**
 * Broad hospitality email ask kits. Copied into the workspace on explicit Add —
 * never auto-seeded.
 */
export const FEEDBACK_MAIL_CATALOG: FeedbackCatalogMailDefinition[] = [
  {
    key: 'friendly-followup',
    category: 'followup',
    locales: {
      de: {
        name: 'Freundliche Nachfrage',
        description:
          'Warmer Dank + Feedback-Link, mit einer Erinnerung nach 3 Tagen.',
        subject: 'Kurzes Feedback zu {{locationName}}?',
        body: `Hallo {{contactName}},

vielen Dank, dass Sie bei {{locationName}} waren. Wir würden uns über ein kurzes Feedback freuen — es dauert nur eine Minute:

{{feedbackUrl}}

Vielen Dank!
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 3,
            subject: 'Erinnerung: Ihr Feedback zu {{locationName}}',
            body: `Hallo {{contactName}},

nur eine kurze Erinnerung — wir freuen uns weiterhin über Ihr Feedback:

{{feedbackUrl}}

Danke!
{{workspaceName}}`,
          },
        ],
      },
      en: {
        name: 'Friendly follow-up',
        description:
          'Warm thank-you plus feedback link, with one reminder after 3 days.',
        subject: 'Quick feedback on {{locationName}}?',
        body: `Hi {{contactName}},

Thanks for visiting {{locationName}}. We would love a quick note — it only takes a minute:

{{feedbackUrl}}

Thank you!
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 3,
            subject: 'Reminder: your feedback on {{locationName}}',
            body: `Hi {{contactName}},

Just a friendly reminder — we still welcome your feedback:

{{feedbackUrl}}

Thanks!
{{workspaceName}}`,
          },
        ],
      },
    },
  },
  {
    key: 'short-direct',
    category: 'direct',
    locales: {
      de: {
        name: 'Kurz & direkt',
        description:
          'Knapp formuliert, ohne Erinnerung — für schnelle Nachfrage.',
        subject: '1 Minute Feedback zu {{locationName}}',
        body: `Hallo {{contactName}},

dürfen wir um ein kurzes Feedback zu {{locationName}} bitten?

{{feedbackUrl}}

Danke
{{workspaceName}}`,
        reminders: [],
      },
      en: {
        name: 'Short & direct',
        description: 'Brief ask with no reminder — for a quick follow-up.',
        subject: '1-minute feedback on {{locationName}}',
        body: `Hi {{contactName}},

May we ask for a quick note about {{locationName}}?

{{feedbackUrl}}

Thanks
{{workspaceName}}`,
        reminders: [],
      },
    },
  },
  {
    key: 'thank-you-ask',
    category: 'followup',
    locales: {
      de: {
        name: 'Danke & Feedback',
        description:
          'Dankbarer Ton mit klarer Bitte — Erinnerung nach 4 Tagen.',
        subject: 'Danke für Ihren Besuch bei {{locationName}}',
        body: `Hallo {{contactName}},

danke, dass Sie bei {{locationName}} zu Gast waren. Ihre Meinung hilft uns, besser zu werden:

{{feedbackUrl}}

Herzlichen Dank
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 4,
            subject: 'Noch kurz: Feedback zu {{locationName}}',
            body: `Hallo {{contactName}},

falls Sie noch einen Moment haben — wir freuen uns über Ihr Feedback zu {{locationName}}:

{{feedbackUrl}}

Danke
{{workspaceName}}`,
          },
        ],
      },
      en: {
        name: 'Thank-you & ask',
        description: 'Grateful tone with a clear ask — reminder after 4 days.',
        subject: 'Thank you for visiting {{locationName}}',
        body: `Hi {{contactName}},

Thank you for being our guest at {{locationName}}. Your thoughts help us improve:

{{feedbackUrl}}

Warmly
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 4,
            subject: 'Still welcome: feedback on {{locationName}}',
            body: `Hi {{contactName}},

If you have a moment — we would still love your feedback on {{locationName}}:

{{feedbackUrl}}

Thanks
{{workspaceName}}`,
          },
        ],
      },
    },
  },
  {
    key: 'event-followup',
    category: 'event',
    locales: {
      de: {
        name: 'Event-Nachfassung',
        description:
          'Nach Event oder Catering — professionell und klar, mit Erinnerung.',
        subject: 'Feedback zu Ihrem Event mit {{workspaceName}}',
        body: `Hallo {{contactName}},

vielen Dank für die Zusammenarbeit bei Ihrem Event. Wir freuen uns über ein kurzes Feedback:

{{feedbackUrl}}

Mit freundlichen Grüßen
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 3,
            subject: 'Erinnerung: Event-Feedback',
            body: `Hallo {{contactName}},

kurze Erinnerung an unser Feedback-Formular zu Ihrem Event:

{{feedbackUrl}}

Vielen Dank
{{workspaceName}}`,
          },
        ],
      },
      en: {
        name: 'Event follow-up',
        description:
          'After an event or catering job — professional, with one reminder.',
        subject: 'Feedback on your event with {{workspaceName}}',
        body: `Hi {{contactName}},

Thank you for working with us on your event. We would appreciate a short note:

{{feedbackUrl}}

Best regards
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 3,
            subject: 'Reminder: event feedback',
            body: `Hi {{contactName}},

A quick reminder about our event feedback form:

{{feedbackUrl}}

Thank you
{{workspaceName}}`,
          },
        ],
      },
    },
  },
  {
    key: 'personal-host',
    category: 'personal',
    locales: {
      de: {
        name: 'Persönlich vom Gastgeber',
        description: 'Persönlicherer Ton — wirkt nah, ohne kitschig zu werden.',
        subject: 'Persönlich: Wie war es bei {{locationName}}?',
        body: `Hallo {{contactName}},

hier schreibt das Team von {{locationName}}. Uns liegt Ihr Eindruck am Herzen — dürften wir um ein kurzes Feedback bitten?

{{feedbackUrl}}

Wir freuen uns auf Ihre Rückmeldung.
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 5,
            subject: 'Noch offen: Ihr Feedback zu {{locationName}}',
            body: `Hallo {{contactName}},

falls es untergegangen ist — hier noch einmal der Link zu Ihrem Feedback:

{{feedbackUrl}}

Danke und bis bald
{{workspaceName}}`,
          },
        ],
      },
      en: {
        name: 'Personal from the host',
        description: 'A more personal tone — warm without being overly salesy.',
        subject: 'From us: how was {{locationName}}?',
        body: `Hi {{contactName}},

This is the team at {{locationName}}. Your impression matters to us — may we ask for a short note?

{{feedbackUrl}}

We look forward to hearing from you.
{{workspaceName}}`,
        reminders: [
          {
            delayDays: 5,
            subject: 'Still open: your feedback on {{locationName}}',
            body: `Hi {{contactName}},

In case this got buried — here is the feedback link again:

{{feedbackUrl}}

Thanks and see you soon
{{workspaceName}}`,
          },
        ],
      },
    },
  },
];

export function listFeedbackMailCatalog(
  locale: FeedbackTemplateLocale,
): ResolvedFeedbackMailCatalogItem[] {
  return FEEDBACK_MAIL_CATALOG.map((item) =>
    resolveFeedbackMailCatalogItem(item, locale),
  );
}

export function getFeedbackMailCatalogItem(
  key: string,
  locale: FeedbackTemplateLocale,
): ResolvedFeedbackMailCatalogItem | null {
  const item = FEEDBACK_MAIL_CATALOG.find((entry) => entry.key === key);
  if (!item) return null;
  return resolveFeedbackMailCatalogItem(item, locale);
}

function resolveFeedbackMailCatalogItem(
  item: FeedbackCatalogMailDefinition,
  preferred: FeedbackTemplateLocale,
): ResolvedFeedbackMailCatalogItem {
  const locale = item.locales[preferred] ? preferred : 'de';
  const localized = item.locales[locale] ?? item.locales.de;
  return {
    key: item.key,
    category: item.category,
    locale,
    name: localized.name,
    description: localized.description,
    subject: localized.subject,
    body: localized.body,
    reminders: localized.reminders,
    availableLocales: Object.keys(item.locales) as FeedbackTemplateLocale[],
  };
}
